import axios from 'axios';
import {
  CUSTOMER_REVIEW_ROUTE_API,
  CUSTOMER_REVIEW_FETCHING,
  CUSTOMER_REVIEW_LIST_FETCH,
  CUSTOMER_REVIEW_DETAIL_FETCH,
  CUSTOMER_REVIEW_ERROR,
  CUSTOMER_REVIEW_ERROR_DISMISS
} from '../constants';

import { logout } from './authAction';
import { apiClient } from '../utils/Api';
import { normalizeCustomerReviewResponse } from '../helpers/customerReviewResponse';
import { normalizeError } from '../utils';

export const DEFAULT_ERROR_MESSAGE = 'Could not load reviews, please try again.';
export const CUSTOMER_REVIEW_TIMEOUT = 15000;

export const FETCH_DETAIL_REQ = 'FETCH_DETAIL_REQ';
export const MODERATE_REQ = 'MODERATE_REQ';

const LOOKUP_TIMEOUT_MS = 15000;
const CREATE_TIMEOUT_MS = 30000;
const UPLOAD_TIMEOUT_MS = 30000;
const DETAIL_TIMEOUT_MS = 15000;
const MODERATE_TIMEOUT_MS = 30000;

let latestRequestId = 0;
let activeRequest = null;

const errorMessageOf = err => {
  const data = (err && err.response && err.response.data) || err || {};
  if (data.message) return data.message;
  if (data.error && data.error.message) return data.error.message;
  return DEFAULT_ERROR_MESSAGE;
};

const onSuccess = payload => ({
  type: CUSTOMER_REVIEW_LIST_FETCH,
  payload
});

const onError = err => ({
  type: CUSTOMER_REVIEW_ERROR,
  payload: { message: errorMessageOf(err) }
});

const isFetch = payload => ({
  type: CUSTOMER_REVIEW_FETCHING,
  payload
});

export const dismissCustomerReviewError = () => ({
  type: CUSTOMER_REVIEW_ERROR_DISMISS
});

/**
 * Get list of customer reviews.
 * @guards jwt-token
 * @param {object?} query axios config, e.g. { params: { status, source, keyword, sort_by, sort_order, per_page, page } }
 */
export const getCustomerReviewList = (query = {}) => dispatch => {
  const requestId = ++latestRequestId;
  if (activeRequest) activeRequest.cancel();

  const source = axios.CancelToken.source();
  activeRequest = { requestId, cancel: source.cancel };
  const isLatest = () => requestId === latestRequestId;
  const finish = () => {
    if (!isLatest()) return;
    dispatch(isFetch({ type: 'fetch', status: false }));
    if (activeRequest && activeRequest.requestId === requestId) activeRequest = null;
  };

  dispatch(isFetch({ type: 'fetch', status: true }));

  const config = {
    ...(query || {}),
    timeout: CUSTOMER_REVIEW_TIMEOUT,
    cancelToken: source.token
  };

  apiClient().get(CUSTOMER_REVIEW_ROUTE_API, config)
    .then(res => {
      if (!isLatest()) return;
      const normalized = normalizeCustomerReviewResponse(res && res.data);
      if (normalized) dispatch(onSuccess(normalized));
      else dispatch(onError(res && res.data));
      finish();
    })
    .catch(err => {
      if (!isLatest()) return;
      if (!axios.isCancel(err)) {
        if (err && err.response && err.response.status === 401) dispatch(logout());
        else dispatch(onError(err));
      }
      finish();
    });

  // Cleanup cancels only this effect's request; a later effect owns the active request.
  return () => {
    if (isLatest() && activeRequest && activeRequest.requestId === requestId) {
      source.cancel();
    }
  };
};

const isUnauthorized = error => (
  error && error.response && error.response.status === 401
);

const runAuthenticatedRequest = request => async dispatch => {
  try {
    return await request();
  } catch (error) {
    if (isUnauthorized(error)) {
      dispatch(logout());
    }

    throw error;
  }
};

export const lookupCustomerReviewOrder = invoiceNumber => runAuthenticatedRequest(
  () => apiClient().get('/admins/sales', {
    params: {
      type: 'processed',
      keyword: invoiceNumber,
      per_page: 1,
    },
    timeout: LOOKUP_TIMEOUT_MS,
  })
);

export const createCustomerReview = payload => runAuthenticatedRequest(
  () => apiClient().post('/admins/customer-reviews', payload, {
    timeout: CREATE_TIMEOUT_MS,
  })
);

export const uploadCustomerReviewImage = payload => runAuthenticatedRequest(
  () => apiClient().post('/admins/customer-reviews/upload-image', payload, {
    timeout: UPLOAD_TIMEOUT_MS,
  })
);

const onProgress = payload => ({
  type: CUSTOMER_REVIEW_FETCHING,
  payload,
});

const onDetailSuccess = payload => ({
  type: CUSTOMER_REVIEW_DETAIL_FETCH,
  payload,
});

const onDetailError = payload => ({
  type: CUSTOMER_REVIEW_ERROR,
  payload,
});

export const getCustomerReviewDetail = id => async dispatch => {
  dispatch(onProgress({ requestType: FETCH_DETAIL_REQ, bool: true }));
  try {
    const res = await apiClient().get(`${CUSTOMER_REVIEW_ROUTE_API}/${id}`, {
      timeout: DETAIL_TIMEOUT_MS,
    });
    dispatch(onDetailSuccess(res.data));
  } catch (error) {
    if (isUnauthorized(error)) {
      dispatch(logout());
      return;
    }
    dispatch(onDetailError({
      requestType: FETCH_DETAIL_REQ,
      id,
      ...normalizeError(error),
    }));
  } finally {
    dispatch(onProgress({ requestType: FETCH_DETAIL_REQ, bool: false }));
  }
};

// Resolves with the response on success, resolves with null on 401 (the admin
// is signed out instead), and rethrows every other error for the caller's alert.
export const moderateCustomerReview = (id, decision) => async dispatch => {
  dispatch(onProgress({ requestType: MODERATE_REQ, bool: true }));
  try {
    const res = await apiClient().put(
      `${CUSTOMER_REVIEW_ROUTE_API}/${id}/${decision}`,
      null,
      { timeout: MODERATE_TIMEOUT_MS }
    );
    dispatch(onDetailSuccess(res.data));
    return res;
  } catch (error) {
    if (isUnauthorized(error)) {
      dispatch(logout());
      return null;
    }
    throw error;
  } finally {
    dispatch(onProgress({ requestType: MODERATE_REQ, bool: false }));
  }
};
